import { gql, useQuery } from '@apollo/client';
import _ from 'lodash';
import { useCurrency, useI18n, useLogger, useSelector } from '~Hooks';

import { getStations } from './__queries__';
import { PrintRow } from '~Components/PrinterManager';
import { DateTime } from 'luxon';

const GET_STATIONS = gql`
  query getStations($companyId: ID!) {
    stations: allStations(filter: { company: { id: $companyId } }) {
      id
      name
      showAllItems
      categories {
        id
        name
      }
      items {
        id
        title
      }
    }
  }
`;

export type KitchenMessage = {
  type?: 'MESSAGE' | 'VOID_ITEMS' | 'VOID_CHEQUE';
  tableName: string;
  staffName?: string;
  message: string;
  items: {
    itemId: string;
    categoryId: string;
    title?: string;
    fraction?: number;
    price?: number;
  }[];
  /** Only passed if it's a VOID_CHEQUE type when a parked order is deleted */
  isParkedOrder?: boolean;
};

export const useKitchenMessage = () => {
  const companyId = useSelector((state) => state.company);
  const printers = useSelector((state) => state.printers);
  const { i18n } = useI18n();
  const currency = useCurrency();
  const logger = useLogger('kitchen-message');

  const query = useQuery<getStations>(GET_STATIONS, {
    skip: !companyId,
    variables: { companyId },
  });

  const stations = query.data?.stations ?? [];

  return ({
    type = 'MESSAGE',
    tableName,
    staffName,
    message,
    items,
    isParkedOrder,
  }: KitchenMessage) => {
    logger.info('Printing kitchen message to table: ' + tableName);

    printers.forEach((printer) => {
      // Kitchen messages are only relevant for stations
      if (printer.stations.length === 0 && type === 'MESSAGE') {
        return;
      }

      const printerStations = stations.filter((station) => printer.stations.includes(station.id));

      const allStationItems = _.flatMap(printerStations, (station) => station.items);
      const allStationCategories = _.flatMap(printerStations, (station) => station.categories);

      const itemsForStation = items.filter((item) => {
        return (
          allStationItems.some((stItem) => stItem.id === item.itemId) ||
          allStationCategories.some(
            (stCat) => stCat.name === item.categoryId || stCat.id === item.categoryId,
          )
        );
      });

      // Void message printing (when cheque or items are removed from cheque)
      if (type === 'VOID_ITEMS' || type === 'VOID_CHEQUE') {
        // If printer is not primary and there are no items for station, return
        if (!printer.primary && itemsForStation.length === 0) {
          logger.warn('No items to print for station', {
            items,
            allStationCategories,
            allStationItems,
          });

          return;
        }

        // If printer is primary, print all items
        const itemsList = printer.primary ? items : itemsForStation;

        const parkedOrderNameContent = `${i18n('Bong.Correction.ParkedOrder')}: '${tableName}'`;
        const tableNameContent = `${i18n('Bong.Correction.Table')}: '${tableName}'`;

        const content: PrintRow[] = [
          { content: ' ' },
          { content: ' ' },
          { content: ' ' },
          { content: ' ' },
          { content: ' ' },
          {
            content: i18n('Bong.Correction'),
            fontSize: 40,
            bold: true,
            doubleHeigth: true,
            doubleWidth: true,
          },
          { content: isParkedOrder ? parkedOrderNameContent : tableNameContent, fontSize: 28 },
          { content: ' ' },
          { content: DateTime.now().toFormat('HH:mm yyyy-MM-dd'), fontSize: 14 },
          {
            content:
              type === 'VOID_ITEMS'
                ? i18n('Bong.Correction.SpecificItems')
                : isParkedOrder
                ? i18n('Bong.Correction.FullParkedOrder')
                : i18n('Bong.Correction.FullCheque'),
            fontSize: 14,
          },
          {
            content: staffName ? i18n('Bong.Correction.Staff') + ': ' + staffName : ' ',
            fontSize: 14,
          },
          { content: i18n('Bong.Correction.Comment') + ': ' + message, fontSize: 14 },
          { content: ' ' },
          {
            content: i18n('Bong.Correction.Removed'),
            fontSize: 40,
            bold: true,
          },
          { content: ' ' },
          ...itemsList.map((item) => {
            const price = item.price ? currency.format(item.price) : '';
            return {
              content: `${item.fraction ?? 1}x ${item.title?.toUpperCase()} ${price}`,
              fontSize: 20,
            };
          }),
          { content: ' ' },
          {
            content: i18n('Bong.Correction.TotalRemoved') + ': ' + itemsList.length,
            fontSize: 14,
          },
          { content: ' ' },
          { content: ' ' },
          { content: ' ' },
          { content: ' ' },
        ];

        logger.info('Printing void ticket... (Table: ' + tableName + ')', content);

        window.printerManager?.initiatePrint({
          printerId: printer.id,
          data: content,
          printType: 'BONG',
        });

        return;
      }

      // Return if there are no items to print
      if (itemsForStation.length === 0) {
        logger.warn('No items to print for station', {
          items,
          printerStations,
          allStationCategories,
          allStationItems,
        });

        return;
      }

      // Regular kitchen messages "Next Dessert" for example
      if (type === 'MESSAGE') {
        const content: PrintRow[] = [
          { content: ' ' },
          { content: ' ' },
          { content: ' ' },
          { content: ' ' },
          { content: ' ' },
          { content: `${i18n('General.Table')} ${tableName}`, fontSize: 28, alignment: 'CENTER' },
          { content: DateTime.now().toFormat('HH:mm'), fontSize: 28, alignment: 'CENTER' },
          { content: ' ' },
          { content: message, fontSize: 28, alignment: 'CENTER' },
          { content: ' ' },
          { content: ' ' },
        ];

        logger.info('Printing message... (Table: ' + tableName + ')', content);

        window.printerManager?.initiatePrint({
          printerId: printer.id,
          data: content,
          printType: 'BONG',
        });
      }
    });
  };
};
